// csvExport.js - Simple CSV Export Utility
// Converts data arrays (with nested objects) to CSV and triggers browser download

/**
 * Recursively flattens a JSON object by concatenating keys for nested properties.
 * E.g., { user: { name: 'Alice' } } -> { user_name: 'Alice' }
 * Arrays are JSON.stringified.
 * * @param {object} obj - The object to flatten.
 * @param {string} [prefix=''] - The prefix for the current keys (used during recursion).
 * @returns {object} The flattened object.
 */
const flattenObject = (obj, prefix = '') => {
    const flattened = {};
  
    for (const key in obj) {
      if (!obj.hasOwnProperty(key)) continue;
  
      // Create the new key name (e.g., 'user_name')
      const newKey = prefix ? `${prefix}_${key}` : key;
      const value = obj[key];
  
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // It's a nested object (but not an array), so recurse
        Object.assign(flattened, flattenObject(value, newKey));
      } else if (Array.isArray(value)) {
        // It's an array, stringify it to avoid [object Object] or [object Array]
        // This allows the data to be placed in a single cell.
        flattened[newKey] = JSON.stringify(value);
      } else {
        // It's a primitive value (string, number, boolean, null, undefined)
        flattened[newKey] = value;
      }
    }
    return flattened;
  };
  
  
  /**
   * Convert array of objects (with nested structures) to a CSV string.
   * It automatically flattens nested objects and determines all unique headers.
   * * @param {Array<object>} data - Array of objects to convert.
   * @param {Array<string>} [columns=null] - Optional array of specific column names to include.
   * @returns {string} CSV formatted string.
   */
  export const arrayToCSV = (data, columns = null) => {
      if (!data || data.length === 0) {
        return '';
      }
  
      // --- 1. Flatten Data ---
      const flattenedData = data.map(item => flattenObject(item));
      
      // --- 2. Determine Headers ---
      let headers;
      
      if (columns && columns.length > 0) {
        // If columns are specified, use them (this is for column filtering/ordering)
        headers = columns;
      } else {
        // If no columns are specified (to avoid skipping data), extract all unique keys from ALL flattened objects
        const allKeys = new Set();
        flattenedData.forEach(item => {
          Object.keys(item).forEach(key => allKeys.add(key));
        });
        headers = Array.from(allKeys);
      }
    
      // Create header row
      const headerRow = headers.join(',');
      
      // --- 3. Create Data Rows ---
      const dataRows = flattenedData.map(item => {
        return headers.map(header => {
          // Get the value from the flattened object
          const value = item[header];
          
          // Handle null/undefined (including missing keys)
          if (value === null || value === undefined) {
            return '';
          }
          
          // Convert to string
          let cellValue = String(value);
          
          // Escape existing double quotes by doubling them
          cellValue = cellValue.replace(/"/g, '""');
          
          // Wrap in quotes if cell contains comma, newline, or a quote (standard CSV escaping)
          if (cellValue.includes(',') || cellValue.includes('\n') || cellValue.includes('"')) {
            cellValue = `"${cellValue}"`;
          }
          
          return cellValue;
        }).join(',');
      });
      
      return [headerRow, ...dataRows].join('\n');
    };
    
    
  /**
   * Trigger browser download of CSV file
   * @param {string} csvContent - CSV formatted string
   * @param {string} filename - Name for downloaded file
   */
  export const downloadCSV = (csvContent, filename) => {
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        // Create download link
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Cleanup
        URL.revokeObjectURL(url);
      }
  };
    
    
  /**
   * Export predictions data to CSV - EXPORTS ALL COLUMNS (including flattened nested data).
   * @param {Array} predictions - Array of prediction objects
   * @param {string} date - Date string for filename
   */
  export const exportPredictions = (predictions, date) => {
      // Calling arrayToCSV without a 'columns' argument ensures all fields are included.
      const csv = arrayToCSV(predictions); 
      const filename = `predictions_${date}.csv`;
      downloadCSV(csv, filename);
  };
    
  /**
   * Export activity data to CSV - EXPORTS ALL COLUMNS (including flattened nested data).
   * @param {Array} activities - Array of activity objects
   * @param {string} date - Date string for filename
   */
  export const exportActivities = (activities, date) => {
      // Calling arrayToCSV without a 'columns' argument ensures all fields are included.
      const csv = arrayToCSV(activities);
      const filename = `activities_${date}.csv`;
      downloadCSV(csv, filename);
  };
    
  /**
   * Export orders data to CSV - EXPORTS ALL COLUMNS (including flattened nested data).
   * @param {Array} orders - Array of order objects
   * @param {string} date - Date string for filename
   */
  export const exportOrders = (orders, date) => {
      // Calling arrayToCSV without a 'columns' argument ensures all fields are included.
      const csv = arrayToCSV(orders);
      const filename = `orders_${date}.csv`;
      downloadCSV(csv, filename);
  };

  export const exportGeneric= (data, date, name) => {
    // Calling arrayToCSV without a 'columns' argument ensures all fields are included.
    const csv = arrayToCSV(data);
    const filename = `${name}_${date}.csv`;
    downloadCSV(csv, filename);
};